import moment from 'moment'
import {useEffect, useMemo, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {Column} from 'react-table'
import {KTCard} from '../../../../../../../../_metronic/helpers'
import {KTable, KTableHeader, KTableLoading} from '../../../../../../components/UI'
import {useKTable} from '../../../../../../contexts/KTableProvider'
import {useTap} from '../../../../../../hooks/useTap'
import {CleaningStatusDto} from '../../../../model/cleaningStatus.model'
import {CLEANING_STATUS_SERVICES} from '../../../../services/cleaning-status.service'
import {useKhipu} from '../../../../../../hooks/useKhipu'
import {ShowSnackBar} from '../../../../../../util/functions'

export const CleaningStatusTable = () => {
  const {
    data,
    pagination,
    setData,
    setPagination,
    updateTable,
    setUpdateTable,
    clearTable,
    setClearTable,
  } = useKTable()
  const {searchValues, clearSearchValues} = useKhipu()
  const {watch, setValue} = useFormContext()
  const campamentoId = watch('CampamentoId')
  const serviceSchedulingId = watch('ServiceSchedulingId')
  const startDate = watch('FechaIni')
  const endDate = watch('FechaFin')
  const searching = watch('searching')
  const searchNumber = watch('searchNumber')
  const closedObservations = watch('closedObservations')
  const observationNumber = watch('observationNumber')
  const [dataTotals, setDataTotals] = useState({
    TotalHabitaciones: 0,
    TotalCompletado: 0,
    TotalPendiente: 0,
    TotalObservado: 0,
    TotalIncompleto: 0,
  })

  const handleSubmit = async (externalFilters: any = {}) => {
    const dto: CleaningStatusDto = {
      CampamentoId: campamentoId,
      ServiceSchedulingId: serviceSchedulingId,
      FechaIni: startDate,
      FechaFin: endDate,
      pageNumber: pagination.currentPage,
      size: pagination.perPage,
      ...searchValues,
      ...externalFilters,
    }
    setValue('searching', true)
    try {
      const [reportStateResponse, totalsResponse] = await Promise.all([
        CLEANING_STATUS_SERVICES.getReportState(dto),
        CLEANING_STATUS_SERVICES.getReportStateTotals(dto),
      ])

      const reportStateData = reportStateResponse.data
      const totalsData = totalsResponse.data

      if (!reportStateData.error && !totalsData.error) {
        setData(reportStateData.data)
        setPagination({
          ...pagination,
          totalResults: reportStateData.total,
        })
        setDataTotals({
          TotalCompletado: totalsData.TotalCompletado,
          TotalHabitaciones: totalsData.TotalHabitaciones,
          TotalObservado: totalsData.TotalObservado,
          TotalPendiente: totalsData.TotalPendiente,
          TotalIncompleto: totalsData.TotalIncompleto,
        })
      }
    } catch (error) {
      console.log({error})
    } finally {
      setValue('searching', false)
    }
  }

  useEffect(() => {
    if (searchNumber > 0) {
      handleSubmit()
    }
  }, [searchNumber, pagination.currentPage, pagination.perPage])

  useEffect(() => {
    if (closedObservations && observationNumber > 0) {
      handleSubmit()
      setValue('closedObservations', false)
      setValue('observationNumber', 0)
    }
  }, [closedObservations, observationNumber])

  const handleTap = useTap()

  const uniqueDates = useMemo(() => {
    if (!data.length) return []

    const dates = new Set<string>()

    data.forEach((row: any) => {
      row.Fechas?.forEach((item: any) => {
        if (item.Fecha) {
          dates.add(item.Fecha)
        }
      })
    })

    return Array.from(dates).sort((a, b) => moment(a).diff(moment(b)))
  }, [data])

  const getTotalCardProps = (onDoubleClick: () => void, title: string, className = '') => ({
    className: `border rounded p-3 h-100 ${className}`.trim(),
    style: {cursor: 'pointer', transition: 'all 0.2s ease-in-out'},
    onDoubleClick,
    title,
    onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.transform = 'translateY(-2px)'
      e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
    },
    onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.transform = 'translateY(0)'
      e.currentTarget.style.boxShadow = 'none'
    },
  })

  const getTotalBoxHouseProps = (onClick: () => void, title: string) => ({
    onClick,
    title,
    onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.transform = 'translateY(-2px)'
      e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
    },
    onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.transform = 'translateY(0)'
      e.currentTarget.style.boxShadow = 'none'
    },
  })

  const handleClickHouse = (cellData: any) => {
    if (
      cellData.estadoCama === 'COMPLETADO' ||
      cellData.estadoCama === 'OBSERVADO' ||
      cellData.estadoCama === 'INCOMPLETO'
    ) {
      setValue('showModalProgrammingServiceDetails', true)
      setValue('cellData', cellData)
    } else {
      ShowSnackBar('La Programación debe estar en estado COMPLETADO u OBSERVADO', 'warning')
    }
  }

  const handleDoubleClickHouse = (cellData: any) => {
    if (cellData.estadoCama === 'COMPLETADO' || cellData.estadoCama === 'OBSERVADO') {
      setValue('showModalProgrammingService', true)
      setValue('cellData', cellData)
    } else {
      ShowSnackBar('La Programación debe estar en estado COMPLETADO u OBSERVADO', 'warning')
    }
  }

  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: (props) => (
          <KTableHeader tableProps={props} title='Módulo' name='Modulo' searcheable />
        ),
        accessor: 'Modulo',
        width: 400,
        className: 'p-0',
      },
      {
        Header: (props) => (
          <KTableHeader tableProps={props} title='Habitación' name='Habitacion' searcheable />
        ),
        accessor: 'Habitacion',
        width: 600,
        className: 'p-0',
      },
      ...uniqueDates.map((date) => ({
        id: date,
        Header: () => {
          const dayLabel = moment(date, 'YYYY-MM-DD').locale('es').format('dd').toUpperCase()
          const dayNumber = moment(date, 'YYYY-MM-DD').format('DD')

          return (
            <div className='text-center w-40 d-flex flex-column align-items-center gap-2'>
              <span className='fs-8 text-muted lh-1'>{dayLabel}</span>
              <span className='lh-1'>{dayNumber}</span>
            </div>
          )
        },
        accessor: (row: any) => {
          return row.Fechas?.find((f: any) => f.Fecha === date)
        },
        className: 'p-1 h-50px',
        Cell: ({value, row}: any) => {
          const fechaData = value

          const estado = fechaData?.Estado || ''
          const escaneoId = fechaData?.EscaneoId || ''

          const colors: Record<string, string> = {
            COMPLETADO: '#b7f7b7',
            PENDIENTE: '#ffb3b3',
            INCOMPLETO: '#ffe680',
            OBSERVADO: '#fff3cd',
            PLANIFICADO: '#b3e5ff',
          }

          const cellData = {
            modulo: row.original.Modulo,
            habitacion: row.original.Habitacion,
            moduloId: row.original.ModuloId,
            habitacionId: row.original.HabitacionId,
            fecha: date,
            estadoCama: estado,
            escaneoId,
          }

          return (
            <div
              className='d-flex justify-content-center align-items-center'
              style={{
                backgroundColor: colors[estado] || '#fff',
                minHeight: '48px',
                width: '100%',
                fontSize: '18px',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                userSelect: 'none',
              }}
              {...getTotalBoxHouseProps(
                () =>
                  handleTap(
                    () => handleClickHouse(cellData),
                    () => handleDoubleClickHouse(cellData),
                  ),
                'Doble click para ver detalles de la programación',
              )}
            >
              🏠
            </div>
          )
        },
      })),
    ],
    [uniqueDates],
  )

  // useEffect(() => {
  //   if (currentTabItem.loadingTable) {
  //     getLaundryList()
  //     changeCurrentTabItem({...currentTabItem, loadingTable: false, laundrySelected: []})
  //   }
  // }, [currentTabItem.loadingTable])

  useEffect(() => {
    if (updateTable) {
      handleSubmit()
      setUpdateTable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTable])
  useEffect(() => {
    if (clearTable) {
      setClearTable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTable])

  useEffect(() => {
    return () => {
      clearSearchValues()
    }
  }, [])

  return (
    <KTCard className='m-5'>
      <div className='m-3 bg-white rounded'>
        {searching && <KTableLoading />}
        {!searching && (
          <>
            <div className='p-4 border-bottom'>
              <div className='row g-3'>
                <div className='col-12 col-md-6 col-lg'>
                  <div
                    {...getTotalCardProps(
                      () => handleSubmit(),
                      'Doble click para ver todas las camas',
                      '',
                    )}
                  >
                    <div className='fs-8' style={{color: '#0d6efd'}}>
                      TOTAL HABITACIONES
                    </div>
                    <div className='fw-bold fs-2' style={{color: '#0d6efd'}}>
                      {dataTotals.TotalHabitaciones}
                    </div>
                  </div>
                </div>
                <div className='col-12 col-md-6 col-lg'>
                  <div
                    {...getTotalCardProps(
                      () => handleSubmit({Estado: 'COMPLETADO'}),
                      'Doble click para filtrar por camas completadas',
                    )}
                  >
                    <div className='text-success fs-8'>COMPLETADAS</div>
                    <div className='fw-bold fs-2 text-success'>{dataTotals.TotalCompletado}</div>
                  </div>
                </div>
                <div className='col-12 col-md-6 col-lg'>
                  <div
                    {...getTotalCardProps(
                      () => handleSubmit({Estado: 'OBSERVADO'}),
                      'Doble click para filtrar por camas observadas',
                    )}
                  >
                    <div className='text-warning fs-8'>OBSERVADAS</div>
                    <div className='fw-bold fs-2 text-warning'>{dataTotals.TotalObservado}</div>
                  </div>
                </div>
                <div className='col-12 col-md-6 col-lg'>
                  <div
                    {...getTotalCardProps(
                      () => handleSubmit({Estado: 'PENDIENTE'}),
                      'Doble click para filtrar por camas pendientes',
                    )}
                  >
                    <div className='text-danger fs-8'>PENDIENTES</div>
                    <div className='fw-bold fs-2 text-danger'>{dataTotals.TotalPendiente}</div>
                  </div>
                </div>
              </div>
            </div>
            <KTable data={data} columns={columns} showPageSizeSelector />
          </>
        )}
      </div>
    </KTCard>
  )
}
